import React from 'react';
import { Button, Grid, Typography } from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { FormTextField } from '@/common/components/form/FormTextField';
import {
  useUpdateProjectMutation,
  ProjectUpdateInput,
} from '@/generated/graphql';
import { useNotification } from '@/common/components/notification/NotificationContext';

interface ProjectEditFormProps {
  projectId: string;
  initialValues: {
    name: string;
    description: string;
    tags: string[];
  };
  onCancel: () => void;
  onSuccess: () => void;
}

export const ProjectEditForm: React.FC<ProjectEditFormProps> = ({
  projectId,
  initialValues,
  onCancel,
  onSuccess,
}) => {
  const [updateProject, { loading: updateLoading }] =
    useUpdateProjectMutation();
  const { showSuccess, showError } = useNotification();

  const handleFormSubmit = async (values: typeof initialValues) => {
    try {
      await updateProject({
        variables: {
          projectId,
          data: values as ProjectUpdateInput,
        },
      });
      showSuccess('Project updated successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error updating project:', error);
      showError('Failed to update project. Please try again.');
    }
  };

  return (
    <Formik
      enableReinitialize
      validateOnBlur
      initialValues={initialValues}
      validationSchema={Yup.object({
        name: Yup.string().required('Project name is required'),
      })}
      onSubmit={handleFormSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        dirty,
        isValid,
        handleSubmit: formikHandleSubmit,
      }) => (
        <Form onSubmit={formikHandleSubmit}>
          <Grid container spacing={2} direction="column" mt={1}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Edit Project Settings
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormTextField
                fullWidth
                name="name"
                label="Project Name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormTextField
                fullWidth
                multiline
                rows={3}
                name="description"
                label="Description"
                data-cy="projectDescription"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Grid>
            <Grid display="flex" size={{ xs: 12 }} gap={{ xs: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={updateLoading || !dirty || !isValid}
                name="saveProjectSettings"
                title="Save Project Settings"
              >
                Save Changes
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={onCancel}
                title="Cancel Editing Project Settings"
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
